<script setup>
import TheCustomerMetaNavigation from '@/components/basic/atoms/TheCustomerMetaNavigation.vue'
import UspBar from '@/components/basic/atoms/UspBar.vue'
import { translate } from '@/plugins/i18n'
import {
  containsPrivateInfo,
  INDEX_FOLLOW,
  NO_INDEX_FOLLOW
} from '@/services/helpers/MetaHelper.js'
import { useCustomerStore } from '@/stores/customer'
import { useManufacturerStore } from '@/stores/manufacturer'
import { useSearchConfig } from '@/stores/searchConfig'
import { useHead } from '@unhead/vue'
import { computed, defineAsyncComponent, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const TheHeader = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheHeader.vue')
)
const TheIntroSection = defineAsyncComponent(
  () => import('@/components/manufacturer/molecules/TheIntroSection.vue')
)
const TheNoProducts = defineAsyncComponent(
  () => import('@/components/manufacturer/atoms/TheNoProducts.vue')
)
const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)
const AppBanner = defineAsyncComponent(
  () => import('@/components/basic/molecules/AppBanner.vue')
)
const AlertBanner = defineAsyncComponent(
  () => import('@/components/basic/atoms/AlertBanner.vue')
)
const TheDescriptionSection = defineAsyncComponent(
  () => import('@/components/manufacturer/molecules/TheDescriptionSection.vue')
)
const TheFilter = defineAsyncComponent(
  () => import('@/components/category/organisms/TheFilter.vue')
)
const MultiTools = defineAsyncComponent(
  () => import('@/components/basic/molecules/MultiTools.vue')
)
const Tool = defineAsyncComponent(
  () => import('@/components/basic/atoms/Tool.vue')
)
const TheFooter = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheFooter.vue')
)

const route = useRoute()
const searchConfig = useSearchConfig()
const manufacturerStore = useManufacturerStore()
const customerStore = useCustomerStore()

const { fetchManufacturerProducts } = searchConfig
const { fetchManufacturer } = manufacturerStore

const adminTool = ref(null)
const bgHeight = ref('')
const introRef = ref(null)

const category = computed(() => searchConfig.category)
const getProducts = computed(() => searchConfig.getProducts)
const getConfig = computed(() => searchConfig.getConfig)
const elbwalkerContext = computed(() => searchConfig.elbwalkerContext)

const manufacturer = computed(() => manufacturerStore.manufacturer)
const calculated = computed(() => manufacturerStore.calculated)
const loading = computed(() => manufacturerStore.loading)
const hasMoreInformation = computed(() => manufacturerStore.hasMoreInformation)

const customerIsAdmin = computed(() => customerStore.customerIsAdmin)

const visible = computed(() => manufacturer.value.status !== 1)
const title = computed(() => manufacturer.value.meta_title)
const description = computed(
  () =>
    manufacturer.value?.meta_description ||
    translate('fallback_meta_description')
)
const robots = computed(() => {
  if (!visible.value) return NO_INDEX_FOLLOW
  return manufacturer.value.robots > 0 ? NO_INDEX_FOLLOW : INDEX_FOLLOW
})
const elbContext = computed(() => {
  const context = elbwalkerContext.value
  context.push(`manufacturer:${manufacturer.value.name}`)
  return context.join(';')
})

const adjustBg = () => {
  if (introRef.value?.$el) {
    bgHeight.value = `height: ${introRef.value.$el.clientHeight - 30}px; max-height: 600px;`
  }
}

const setup = (id) => {
  fetchManufacturer(id)
  fetchManufacturerProducts(id)
}

onMounted(async () => {
  const { TYPES } = await import('@/constants/AdminTool')
  adminTool.value = TYPES

  if (route.params.id) {
    setup(route.params.id)
  }
})

useHead({
  title,
  meta: [
    {
      name: 'robots',
      content: computed(() =>
        containsPrivateInfo() ? NO_INDEX_FOLLOW : robots.value
      )
    },
    { name: 'description', content: description }
  ],
  link: [
    {
      rel: 'canonical',
      href: computed(
        () => `${location.origin}/${manufacturer.value.full_url || route}`
      )
    }
  ]
})
</script>

<template>
  <div id="content" itemscope itemtype="https://schema.org/manufacturer">
    <AppBanner />
    <AlertBanner
      v-if="!visible"
      :text="translate('alert_message_preview_mode')"
    />
    <UspBar />
    <TheCustomerMetaNavigation />
    <TheHeader />
    <transition name="fade">
      <main
        id="page-template"
        class="position-relative"
        data-elbglobals="page_type:manufacturer"
        :data-elbcontext="elbContext"
      >
        <img
          class="bg-image d-none"
          :class="{ 'd-md-block': calculated.bgImage }"
          :src="calculated.bgImage"
          alt="manufacturer background"
          :style="bgHeight"
        />
        <TheIntroSection
          class="v-container px-0 pt-2"
          ref="introRef"
          @loaded="adjustBg"
        />
        <div :loading="loading" :class="{ 'v-container': loading }">
          <div
            id="manufacturer-products"
            class="headline-container text-center py-2"
          >
            <h2
              v-html="translate('products_by_manufaturer', manufacturer.name)"
            />
          </div>
          <LazyComponent id="manufacturer-filter" :delay="1000">
            <TheFilter v-if="getProducts.length" class="px-md-0" />
            <TheNoProducts v-else />
          </LazyComponent>
          <div v-if="hasMoreInformation">
            <div class="v-container px-0 pt-2">
              <div
                id="manufacturer-description-section"
                class="w-100 headline-container py-5 text-center"
              >
                <h2 v-html="translate('manufacturer_more_about_winery')" />
              </div>
              <TheDescriptionSection />
            </div>
          </div>
        </div>
        <MultiTools id="fixed-tools" v-if="customerIsAdmin && adminTool">
          <Tool :type="adminTool.EDIT_CATEGORY" :id="category.code" />
          <Tool :type="adminTool.EDIT_MANUFACTURER" :id="$route.params.id" />
          <Tool :type="adminTool.SHOW_ELBWALKER_USER_DATA" />
        </MultiTools>
      </main>
    </transition>
    <LazyComponent id="footer" :delay="1500">
      <TheFooter />
    </LazyComponent>
  </div>
</template>

<style scoped>
:deep(.headline-container h2) {
  font-size: var(--xxxl-font-size);
  font-weight: normal;
  display: inline-block;
  padding: 10px 30px;
  position: relative;
  letter-spacing: 1px;
}

@media screen and (min-width: 768px) {
  :deep(.headline-container h2:before) {
    right: 100%;
  }
  :deep(.headline-container h2:after) {
    left: 100%;
  }

  :deep(.headline-container h2:before),
  :deep(.headline-container h2:after) {
    background: rgb(var(--v-theme-beige));
    content: '';
    display: block;
    height: 2px;
    position: absolute;
    top: 50%;
    width: 20vw;
  }
}

.bg-image {
  object-fit: cover;
  transition: max-height var(--transition-duration-xxxxl);
  min-height: 390px;
  max-height: 0;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

#manufacturer-products {
  margin-block: 10px;
  @media screen and (min-width: 768px) {
    margin-block: 50px;
  }
}
</style>
